import React from "react";
import {
  pizzas,
  gyros,
  gyrosExtras,
  desserts,
  sides,
  drinks,
} from "../data/menuData";
import "../styles/pizzaMenu.css";

/** Split array into chunks of N */
function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/** PRICE RENDER FIX — supports numbers + strings ("Hamarosan") */
function renderPrice(price) {
  if (typeof price === "number") {
    return `${price.toLocaleString("hu-HU")} Ft`;
  }
  return price; // e.g. "Hamarosan"
}

function MenuLine({ name, desc, price }) {
  return (
    <li className="menu-item">
      <div className="menu-title-row">
        <span className="menu-name">{name}</span>
        <span className="menu-dot" aria-hidden>
          •
        </span>
        <span className="menu-price">{renderPrice(price)}</span>
      </div>
      {desc && <div className="menu-desc">{desc}</div>}
    </li>
  );
}

/** Drinks line — supports number + string price */
function DrinkLine({ name, price }) {
  const match = name.match(/^(.*)\s*\(([^)]+)\)\s*$/);
  const display = match ? match[1].trim() : name;
  const sub = match ? match[2] : null;

  return (
    <li className="menu-item text-center">
      <div className="menu-title-row">
        <span className="menu-name">{display}</span>
        <span className="menu-dot" aria-hidden>
          •
        </span>
        <span className="menu-price">{renderPrice(price)}</span>
      </div>
      {sub && <div className="menu-desc">{sub}</div>}
    </li>
  );
}

/** Alternating section */
function AlternatingSection({
  id,
  kicker,
  title,
  items,
  note,
  imageSrcForIndex,
  perBlock = 4,
  reverseFirst = false,
}) {
  const groups = chunk(items, perBlock);

  return (
    <section id={id} className="pizza-menu container-fluid py-5">
      <div className="container">
        {kicker && <div className="section-kicker text-center">{kicker}</div>}
        {title && <h2 className="section-title text-center mb-4">{title}</h2>}
        {note && <p className="menu-note text-center mb-5">{note}</p>}

        {groups.map((group, idx) => {
          const imageLeft = (idx % 2 === 0) !== reverseFirst;
          const hasImage = typeof imageSrcForIndex === "function";

          return (
            <div
              className={`row align-items-center g-5 mb-5 ${
                hasImage ? "" : "justify-content-center"
              }`}
              key={`${id}-${idx}`}
            >
              {/* IMAGE */}
              {hasImage && (
                <div className={`col-lg-6 ${imageLeft ? "" : "order-lg-2"}`}>
                  <div className="pizza-img-wrap">
                    <img
                      src={imageSrcForIndex(idx)}
                      alt={`${title || "Kép"} ${idx + 1}`}
                      className="img-fluid pizza-hero-img"
                      loading="lazy"
                    />
                  </div>
                </div>
              )}

              {/* LIST */}
              <div
                className={`${hasImage ? "col-lg-6" : "col-lg-8"} ${
                  imageLeft ? "" : "order-lg-1"
                }`}
              >
                <ul className="menu-list">
                  {group.map((p, i) => (
                    <MenuLine key={p.name + i} {...p} />
                  ))}
                </ul>

                <div className="text-center mt-4">
                  <a
                    className="btn btn-order rounded-pill px-4"
                    href="https://debrecenipizza.hu/"
                    rel="noopener"
                  >
                    Hamarosan
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** Drinks section */
function DrinksSection({ id, title, items }) {
  return (
    <section id={id} className="pizza-menu container-fluid py-5">
      <div className="container">
        <h2 className="section-title text-center mb-4">{title}</h2>

        <div className="row justify-content-center">
          <div className="col-lg-8">
            <ul className="menu-list">
              {items.map((d, i) => (
                <DrinkLine key={d.name + i} {...d} />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Bottom note — Debrecen localized */
function BottomNote() {
  return (
    <section className="pizza-menu container-fluid py-4">
      <div className="container">
        <p className="menu-bottom text-center">
          Kiszállítási információk:{" "}
          <strong>Hamarosan elérhető Debrecenben!</strong>
        </p>

        <p className="menu-bottom-red text-center">
          A változtatás jogát fenntartjuk!
        </p>

        <p className="menu-bottom text-center">
          Allergén információk hamarosan elérhetők lesznek.
        </p>
      </div>
    </section>
  );
}

export default function PizzaMenu() {
  return (
    <>
      {/* PIZZAS */}
      <AlternatingSection
        id="pizzaink"
        title="Pizzáink"
        note="Pizzáink választható tésztavastagsággal (1 és 2 mm) készülnek, 28 cm-es méretben."
        items={pizzas}
        perBlock={4}
        imageSrcForIndex={(i) =>
          `/images/menu/pizzas/pizza-${(i % 3) + 1}.webp`
        }
      />

      {/* GYROS */}
      <AlternatingSection
        id="gyrosaink"
        title="Gyrosok"
        items={[...gyros, ...gyrosExtras]}
        perBlock={4}
        reverseFirst={true}
        imageSrcForIndex={(i) => `/images/menu/gyros/gyros-${(i % 2) + 1}.webp`}
      />

      {/* DESSERTS */}
      <AlternatingSection
        id="oriáspalacsintaink"
        kicker="Újdonságok"
        title="Óriáspalacsinták"
        items={desserts}
        perBlock={6}
        reverseFirst={true}
        imageSrcForIndex={() => `/images/menu/pancakes/pancake-1.webp`}
      />

      {/* SIDES */}
      <AlternatingSection
        id="koreteink"
        title="Köretek"
        items={sides}
        perBlock={3}
        imageSrcForIndex={() => `/images/menu/sides/side-1.webp`}
      />

      {/* DRINKS */}
      <DrinksSection id="uditoink" title="Üdítők" items={drinks} />

      {/* FINAL NOTE */}
      <BottomNote />
    </>
  );
}
